// ===========================================
// School Info Tab
// Phase 2 Priority 3 - Admin Settings Enhancements
// ===========================================

import { useEffect, useRef, useState } from 'react';
import { formatAddress } from '../../../constants/countries';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  Stack,
  Typography,
  Alert,
  Skeleton,
  InputAdornment,
  IconButton,
  Tooltip,
  CircularProgress,
  Switch,
  FormControlLabel,
  Autocomplete,
  Checkbox,
  FormGroup,
} from '@mui/material';
import {
  Save as SaveIcon,
  Language as DomainIcon,
  Email as EmailIcon,
  RestartAlt as ResetIcon,
  School as SchoolIcon,
  Phone as PhoneIcon,
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
  Public as TimezoneIcon,
  Description as DescriptionIcon,
  FormatQuote as FormatQuoteIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import {
  useSchoolInfo,
  useUpdateSchoolInfo,
  useResetSchoolInfo,
} from '../../../hooks/useAdminSettings';
import { useLocations } from '../../../hooks/useAdmin';
import * as adminSettingsApi from '../../../api/adminSettings.api';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { adminSettingsKeys } from '../../../hooks/useAdminSettings';
import SettingsWriteGate from './SettingsWriteGate';

// ===========================================
// TYPES
// ===========================================

interface SchoolInfoFormData {
  schoolName: string;
  description: string;
  tagline: string;
  contactNumber: string;
  domain: string;
  replyToEmail: string;
  timezone: string;
  requireWorkingWithChildrenId: boolean;
  mainLocationId: string;
  /** JS day numbers, 0 = Sunday. */
  officeDays: number[];
}

const DEFAULT_OFFICE_DAYS = [1, 2, 3, 4, 5];
// Monday first, as the week is read in Australia; values are the JS day numbers the server stores.
const OFFICE_DAY_OPTIONS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' },
];

// ===========================================
// VALIDATION
// ===========================================

const DOMAIN_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Common timezones grouped by region
const TIMEZONES = [
  { region: 'Australia & Pacific', zones: [
    { value: 'Australia/Sydney', label: 'Sydney (AEDT/AEST)' },
    { value: 'Australia/Melbourne', label: 'Melbourne (AEDT/AEST)' },
    { value: 'Australia/Brisbane', label: 'Brisbane (AEST)' },
    { value: 'Australia/Perth', label: 'Perth (AWST)' },
    { value: 'Australia/Adelaide', label: 'Adelaide (ACDT/ACST)' },
    { value: 'Australia/Hobart', label: 'Hobart (AEDT/AEST)' },
    { value: 'Australia/Darwin', label: 'Darwin (ACST)' },
    { value: 'Pacific/Auckland', label: 'Auckland (NZDT/NZST)' },
  ]},
  { region: 'Asia', zones: [
    { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
    { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Asia/Seoul', label: 'Seoul (KST)' },
    { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
    { value: 'Asia/Dubai', label: 'Dubai (GST)' },
    { value: 'Asia/Kolkata', label: 'India (IST)' },
  ]},
  { region: 'Europe', zones: [
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
    { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
    { value: 'Europe/Rome', label: 'Rome (CET/CEST)' },
    { value: 'Europe/Madrid', label: 'Madrid (CET/CEST)' },
    { value: 'Europe/Amsterdam', label: 'Amsterdam (CET/CEST)' },
    { value: 'Europe/Dublin', label: 'Dublin (GMT/IST)' },
  ]},
  { region: 'Americas', zones: [
    { value: 'America/New_York', label: 'New York (EST/EDT)' },
    { value: 'America/Chicago', label: 'Chicago (CST/CDT)' },
    { value: 'America/Denver', label: 'Denver (MST/MDT)' },
    { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
    { value: 'America/Toronto', label: 'Toronto (EST/EDT)' },
    { value: 'America/Vancouver', label: 'Vancouver (PST/PDT)' },
    { value: 'America/Mexico_City', label: 'Mexico City (CST/CDT)' },
    { value: 'America/Sao_Paulo', label: 'São Paulo (BRT)' },
  ]},
  { region: 'Africa & Middle East', zones: [
    { value: 'Africa/Johannesburg', label: 'Johannesburg (SAST)' },
    { value: 'Africa/Cairo', label: 'Cairo (EET)' },
    { value: 'Africa/Lagos', label: 'Lagos (WAT)' },
    { value: 'Africa/Nairobi', label: 'Nairobi (EAT)' },
  ]},
];

// Flatten timezones for autocomplete
const TIMEZONE_OPTIONS = TIMEZONES.flatMap(group =>
  group.zones.map(zone => ({ ...zone, region: group.region }))
);

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function SchoolInfoTab() {
  const { data: settings, isLoading, error } = useSchoolInfo();
  const { data: locations, isLoading: locationsLoading } = useLocations();
  const updateMutation = useUpdateSchoolInfo();
  const resetMutation = useResetSchoolInfo();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  // Logo upload state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isDirty },
  } = useForm<SchoolInfoFormData>({
    defaultValues: {
      schoolName: '',
      description: '',
      tagline: '',
      contactNumber: '',
      domain: '',
      replyToEmail: '',
      timezone: 'Australia/Sydney',
      requireWorkingWithChildrenId: false,
      mainLocationId: '',
      officeDays: DEFAULT_OFFICE_DAYS,
    },
  });

  // Seed the form from the server, but NEVER over unsaved edits.
  //
  // ⚠️ This effect fires on every `settings` CHANGE, not just the first load —
  // and `handleLogoUpload` deliberately invalidates that query so the new logo
  // appears. The refetch therefore reset the whole form and DISCARDED whatever
  // the admin had typed: enter a school name, upload a logo, and the name
  // silently reverts to the placeholder (Andrew, staging 2026-09-08).
  //
  // `isDirty` is the guard: once the admin has touched the form, the server
  // copy must not overwrite it. A save clears `isDirty` (the mutation's
  // onSuccess resets), so the next refetch seeds normally.
  useEffect(() => {
    if (settings && !isDirty) {
      reset({
        schoolName: settings.schoolName || '',
        description: settings.description || '',
        tagline: settings.tagline || '',
        contactNumber: settings.contactNumber || '',
        domain: settings.domain || '',
        replyToEmail: settings.replyToEmail || '',
        timezone: settings.timezone || 'Australia/Sydney',
        requireWorkingWithChildrenId: settings.requireWorkingWithChildrenId || false,
        mainLocationId: settings.mainLocationId || '',
        officeDays: settings.officeDays?.length ? settings.officeDays : DEFAULT_OFFICE_DAYS,
      });
    }
    // `isDirty` is deliberately NOT a dependency: adding it would re-run this
    // the moment the form goes clean again and re-introduce the overwrite.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, reset]);

  const onSubmit = (data: SchoolInfoFormData) => {
    // ⚠️ `reset(data)` on success is what clears `isDirty`. The seeding effect
    // above skips while the form is dirty, so without this the form would stay
    // dirty forever after the first save and never pick up a server-side change
    // again — trading the overwrite bug for a staleness one.
    updateMutation.mutate({
      schoolName: data.schoolName || null,
      description: data.description || null,
      tagline: data.tagline || null,
      contactNumber: data.contactNumber || null,
      domain: data.domain || null,
      replyToEmail: data.replyToEmail || null,
      timezone: data.timezone,
      requireWorkingWithChildrenId: data.requireWorkingWithChildrenId,
      mainLocationId: data.mainLocationId || null,
      officeDays: [...data.officeDays].sort((a, b) => a - b),
    }, {
      // Mark the form clean at the values we just persisted.
      onSuccess: () => reset(data),
    });
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      await adminSettingsApi.uploadSchoolLogo(file);
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.schoolInfo() });
      enqueueSnackbar('School logo uploaded', { variant: 'success' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to upload logo';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleLogoRemove = async () => {
    if (!window.confirm('Remove the school logo?')) return;

    setIsRemoving(true);
    try {
      await adminSettingsApi.removeSchoolLogo();
      queryClient.invalidateQueries({ queryKey: adminSettingsKeys.schoolInfo() });
      enqueueSnackbar('School logo removed', { variant: 'success' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove logo';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setIsRemoving(false);
    }
  };

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={200} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load school info settings. Please try again.
      </Alert>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {/* School Identity Section */}
        <Card variant="outlined">
          <CardHeader
            title="School Identity"
            subheader="Your school's name, contact number, and logo"
            avatar={<SchoolIcon color="primary" />}
          />
          <CardContent>
            <Stack spacing={3}>
              <Controller
                name="schoolName"
                control={control}
                rules={{
                  maxLength: {
                    value: 100,
                    message: 'School name must be 100 characters or less',
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="School Name"
                    placeholder="Music 'n Me"
                    fullWidth
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error?.message ||
                      'Your school\'s display name'
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SchoolIcon color="action" fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
              <Controller
                name="description"
                control={control}
                rules={{
                  maxLength: {
                    value: 500,
                    message: 'Description must be 500 characters or less',
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="School Description"
                    placeholder="A brief description of your music school..."
                    fullWidth
                    multiline
                    rows={3}
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error?.message ||
                      'Shown on public pages like the Schools Directory'
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                          <DescriptionIcon color="action" fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                      },
                    }}
                  />
                )}
              />
              <Controller
                name="tagline"
                control={control}
                rules={{
                  maxLength: {
                    value: 200,
                    message: 'Tagline must be 200 characters or less',
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Tagline"
                    placeholder="Where Every Note Matters"
                    fullWidth
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error?.message ||
                      'A short line shown under your logo on invoices, permission slips and email footers'
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FormatQuoteIcon color="action" fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
              <Controller
                name="contactNumber"
                control={control}
                rules={{
                  maxLength: {
                    value: 30,
                    message: 'Contact number must be 30 characters or less',
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Main Contact Number"
                    placeholder="+61 2 1234 5678"
                    fullWidth
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error?.message ||
                      'Primary phone number for the school'
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon color="action" fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              {/* Timezone Selector */}
              <Controller
                name="timezone"
                control={control}
                rules={{
                  required: 'Timezone is required',
                }}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    value={TIMEZONE_OPTIONS.find(opt => opt.value === field.value) || null}
                    onChange={(_, newValue) => {
                      field.onChange(newValue?.value || 'Australia/Sydney');
                    }}
                    options={TIMEZONE_OPTIONS}
                    groupBy={(option) => option.region}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Timezone"
                        error={!!fieldState.error}
                        helperText={
                          fieldState.error?.message ||
                          'School timezone for accurate email quiet hours and scheduling'
                        }
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <TimezoneIcon color="action" fontSize="small" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: 'divider',
                              borderWidth: 1.5,
                            },
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                              borderWidth: 2,
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: 'primary.main',
                              borderWidth: 2,
                            },
                          },
                        }}
                      />
                    )}
                    componentsProps={{
                      paper: {
                        sx: {
                          border: '1.5px solid',
                          borderColor: 'divider',
                          boxShadow: 3,
                        },
                      },
                    }}
                    renderOption={(props, option) => (
                      <li {...props} key={option.value}>
                        <Box>
                          <Typography variant="body2">{option.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.value}
                          </Typography>
                        </Box>
                      </li>
                    )}
                  />
                )}
              />

              {/* Main Location Selector */}
              <Controller
                name="mainLocationId"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    value={locations?.find(loc => loc.id === field.value) || null}
                    onChange={(_, newValue) => {
                      field.onChange(newValue?.id || '');
                    }}
                    options={locations?.filter(loc => loc.isActive) || []}
                    getOptionLabel={(option) => option.name}
                    loading={locationsLoading}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Main Location"
                        placeholder="Select a location..."
                        helperText="Displayed on public pages like the Schools Directory"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <LocationIcon color="action" fontSize="small" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => {
                      // Format the STRUCTURED fields, as the Locations table and the
                      // public directory do. `Location.address` is deprecated and
                      // nothing writes it any more, so reading it showed the original
                      // seed text forever and never reflected an edit (found in manual
                      // testing, 2026-09-07).
                      const optionAddress = formatAddress({
                        unit: option.unit,
                        streetAddress: option.streetAddress,
                        suburb: option.suburb,
                        state: option.state,
                        postcode: option.postcode,
                        country: option.country,
                      });
                      return (
                        <li {...props} key={option.id}>
                          <Box>
                            <Typography variant="body2">{option.name}</Typography>
                            {optionAddress && (
                              <Typography variant="caption" color="text.secondary">
                                {optionAddress}
                              </Typography>
                            )}
                          </Box>
                        </li>
                      );
                    }}
                  />
                )}
              />

              {/* Office days: next-action due dates count these only (enquiries plan L4) */}
              <Controller
                name="officeDays"
                control={control}
                rules={{ validate: (v) => v.length > 0 || 'Choose at least one office day' }}
                render={({ field, fieldState }) => (
                  <Box>
                    <Typography variant="subtitle2" id="office-days-label">
                      Office days
                    </Typography>
                    <FormGroup row aria-labelledby="office-days-label">
                      {OFFICE_DAY_OPTIONS.map((day) => (
                        <FormControlLabel
                          key={day.value}
                          label={day.label}
                          control={
                            <Checkbox
                              checked={field.value.includes(day.value)}
                              onChange={(e) =>
                                field.onChange(
                                  e.target.checked
                                    ? [...field.value, day.value]
                                    : field.value.filter((d) => d !== day.value),
                                )
                              }
                            />
                          }
                        />
                      ))}
                    </FormGroup>
                    <Typography variant="caption" color={fieldState.error ? 'error' : 'text.secondary'} display="block">
                      {fieldState.error?.message ||
                        'Next-action due dates on enquiries count office days only.'}
                    </Typography>
                  </Box>
                )}
              />

              {/* Working with Children ID Toggle */}
              <Box sx={{ mt: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={watch('requireWorkingWithChildrenId') || false}
                      onChange={(e) =>
                        setValue('requireWorkingWithChildrenId', e.target.checked, { shouldDirty: true })
                      }
                    />
                  }
                  label="Require Working with Children ID"
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                  When enabled, teachers will be required to provide a Working with Children check ID
                  (e.g., WWCC in Australia, DBS in UK). This is a country-specific compliance requirement.
                </Typography>
              </Box>

              {/* School Logo Upload */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  School Logo
                </Typography>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleLogoUpload}
                  style={{ display: 'none' }}
                  disabled={isUploading}
                />
                <Stack direction="row" spacing={2} alignItems="center">
                  {settings?.logoUrl ? (
                    <Box
                      component="img"
                      src={settings.logoUrl}
                      alt="School logo"
                      sx={{
                        maxWidth: 160,
                        maxHeight: 80,
                        objectFit: 'contain',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 1,
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 160,
                        height: 80,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px dashed',
                        borderColor: 'divider',
                        borderRadius: 1,
                        color: 'text.secondary',
                      }}
                    >
                      <Typography variant="caption">No logo</Typography>
                    </Box>
                  )}
                  <SettingsWriteGate fallback={null}>
                  <Stack spacing={1}>
                    <Tooltip title="Upload logo">
                      <span>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={isUploading ? <CircularProgress size={16} /> : <PhotoCameraIcon />}
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isUploading || isRemoving}
                        >
                          {settings?.logoUrl ? 'Change' : 'Upload'}
                        </Button>
                      </span>
                    </Tooltip>
                    {settings?.logoUrl && (
                      <Tooltip title="Remove logo">
                        <span>
                          <IconButton
                            color="error"
                            size="small"
                            onClick={handleLogoRemove}
                            disabled={isUploading || isRemoving}
                          >
                            {isRemoving ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Stack>
                  </SettingsWriteGate>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  JPG, PNG, or WebP. Max 5MB. Will be resized to 400x400 max.
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Domain Name Section */}
        <Card variant="outlined">
          <CardHeader
            title="School Domain"
            subheader="Configure your school's custom domain for future multi-school support"
            avatar={<DomainIcon color="primary" />}
          />
          <CardContent>
            <Stack spacing={2}>
              <Controller
                name="domain"
                control={control}
                rules={{
                  pattern: {
                    value: DOMAIN_REGEX,
                    message: 'Please enter a valid domain (e.g., myschool.musicnme.com.au)',
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Domain Name"
                    placeholder="myschool.musicnme.com.au"
                    fullWidth
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error?.message ||
                      'Your school\'s subdomain or custom domain'
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <DomainIcon color="action" fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
              <Alert severity="info" icon={false}>
                <Typography variant="body2">
                  <strong>Coming Soon:</strong> Custom domains will allow each school
                  to have their own branded URL. This is being prepared for future
                  multi-school expansion.
                </Typography>
              </Alert>
            </Stack>
          </CardContent>
        </Card>

        {/* Reply-To Email Section */}
        <Card variant="outlined">
          <CardHeader
            title="System Email Settings"
            subheader="Configure the reply-to address for system notifications"
            avatar={<EmailIcon color="primary" />}
          />
          <CardContent>
            <Stack spacing={2}>
              <Controller
                name="replyToEmail"
                control={control}
                rules={{
                  pattern: {
                    value: EMAIL_REGEX,
                    message: 'Please enter a valid email address',
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Reply-To Email"
                    placeholder="hello@musicnme.com.au"
                    fullWidth
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error?.message ||
                      'Recipients will reply to this address when responding to system emails'
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon color="action" fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
              <Alert severity="info" icon={false}>
                <Typography variant="body2">
                  This email will be used as the "Reply-To" address for all automated
                  notifications sent from the system, including:
                </Typography>
                <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                  <li>Lesson reminders</li>
                  <li>Invoice notifications</li>
                  <li>Meet & Greet confirmations</li>
                  <li>Password reset emails</li>
                </ul>
              </Alert>
            </Stack>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <SettingsWriteGate>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ResetIcon />}
              onClick={() => resetMutation.mutate()}
              disabled={resetMutation.isPending || updateMutation.isPending}
            >
              {resetMutation.isPending ? 'Resetting...' : 'Clear All'}
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={!isDirty || updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </Stack>
        </SettingsWriteGate>
      </Stack>
    </Box>
  );
}
